import React, { FC } from "react";
import { Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import { formatCurrency } from "../../utils/formatCurrency";

// Shape shared by live pricing_cost_line_items rows and the frozen
// baseCostLineItems / package line_items arrays on snapshots.
export interface CostLineItemLike {
    label: string;
    hours: string | number | null;
    rate: string | number | null;
    cost: string | number;
    notes?: string | null;
}

const num = (v: string | number | null | undefined): number =>
    v == null || v === "" ? 0 : Number(v);

/**
 * Read-only BILL-side cost sheet: role hours × rates plus flat items, with
 * labor and cost-basis totals. Renders the given line items (a content
 * type's live sheet, or the frozen sheet on a quote/package snapshot).
 * No static fallback — the old deck recipe held internal cost rates.
 */
export const BaseRecipeTable: FC<{ items?: CostLineItemLike[] | null }> = ({ items }) => {
    const rows = items ?? [];
    if (rows.length === 0) {
        return (
            <Typography sx={{ color: "var(--text-2)", fontSize: 13, fontStyle: "italic", my: 1 }}>
                No cost sheet configured.
            </Typography>
        );
    }
    const hourly = rows.filter((r) => r.hours != null);
    const flat = rows.filter((r) => r.hours == null);
    const laborHours = hourly.reduce((s, r) => s + num(r.hours), 0);
    const laborCost = hourly.reduce((s, r) => s + num(r.cost), 0);
    const totalCost = rows.reduce((s, r) => s + num(r.cost), 0);

    return (
        <Table size="small" sx={{ my: 1, maxWidth: 640 }}>
            <TableHead>
                <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Role</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align="right">
                        Hours
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align="right">
                        Rate
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align="right">
                        Cost
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Notes</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {hourly.map((r, i) => (
                    <TableRow key={`${r.label}-${i}`}>
                        <TableCell sx={{ fontSize: 13 }}>{r.label}</TableCell>
                        <TableCell sx={{ fontSize: 13 }} align="right">
                            {num(r.hours)}
                        </TableCell>
                        <TableCell sx={{ fontSize: 13 }} align="right">
                            ${num(r.rate)}/hr
                        </TableCell>
                        <TableCell sx={{ fontSize: 13 }} align="right">
                            {formatCurrency(num(r.cost))}
                        </TableCell>
                        <TableCell sx={{ fontSize: 13, color: "var(--text-2)" }}>{r.notes}</TableCell>
                    </TableRow>
                ))}
                <TableRow>
                    <TableCell sx={{ fontWeight: 700, fontSize: 13 }}>Total labor</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: 13 }} align="right">
                        {laborHours}
                    </TableCell>
                    <TableCell />
                    <TableCell sx={{ fontWeight: 700, fontSize: 13 }} align="right">
                        {formatCurrency(laborCost)}
                    </TableCell>
                    <TableCell />
                </TableRow>
                {flat.map((r, i) => (
                    <TableRow key={`flat-${r.label}-${i}`}>
                        <TableCell sx={{ fontWeight: 700, fontSize: 13 }}>+ {r.label}</TableCell>
                        <TableCell />
                        <TableCell />
                        <TableCell sx={{ fontWeight: 700, fontSize: 13 }} align="right">
                            {formatCurrency(num(r.cost))}
                        </TableCell>
                        <TableCell sx={{ fontSize: 13, color: "var(--text-2)" }}>{r.notes}</TableCell>
                    </TableRow>
                ))}
                <TableRow>
                    <TableCell sx={{ fontWeight: 700, fontSize: 13 }}>Total cost basis</TableCell>
                    <TableCell />
                    <TableCell />
                    <TableCell sx={{ fontWeight: 700, fontSize: 13 }} align="right">
                        {formatCurrency(totalCost)}
                    </TableCell>
                    <TableCell />
                </TableRow>
            </TableBody>
        </Table>
    );
};

export default BaseRecipeTable;
